/**
 * Anonymous usage statistics (milestone 7, slice 12), with PostHog on its EU host. It is off in three
 * ways: without a key in the build (`VITE_POSTHOG_KEY`) nothing is loaded or sent; without the player's
 * explicit yes (the `analytics` consent of the privacy screen) nothing is loaded either; and a yes
 * that is taken back stops it at once. The library is a chunk of its own, fetched only when both
 * hold. Nothing identifies the player: no account id, no name, no IP address kept, no cookie or
 * storage (the identifier lives in memory, for the visit), no autocapture, no recording. What is
 * sent is a short list of events with a few facts about the game (`AnalyticsEvent`).
 */

export type AnalyticsValue = string | number | boolean;
export type AnalyticsProps = Record<string, AnalyticsValue>;

/** Every event the app sends; adding one is a decision (and a line in the privacy notice). */
export type AnalyticsEvent =
  /** The app was opened (once per visit). `screen` is the first part of the address. */
  | 'app_opened'
  /** The player went to another part of the app. */
  | 'screen_view'
  /** A heist began: `mode`, and for the ones the player chose, `map` and `difficulty`. */
  | 'heist_started'
  /** A heist ended: `mode`, `success`, and `minutes` it lasted (rounded). */
  | 'heist_ended';

/** The part of PostHog the app uses. */
export interface AnalyticsSink {
  capture: (event: string, props?: Record<string, unknown>) => void;
  opt_out_capturing: () => void;
  opt_in_capturing: () => void;
  reset: () => void;
}

export interface AnalyticsEnv {
  VITE_POSTHOG_KEY?: string | undefined;
  VITE_POSTHOG_HOST?: string | undefined;
}

export const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';

/** Whether this build has a key: with none, analytics is not there at all (and the privacy screen need not offer it). */
export function analyticsConfigured(env: AnalyticsEnv = import.meta.env): boolean {
  return (env.VITE_POSTHOG_KEY?.trim() ?? '') !== '';
}

let sink: AnalyticsSink | null = null;
let loading: Promise<void> | null = null;
let wanted = false;
/** Events sent while the library is still loading (after a yes), kept for the moment it is ready. */
let pending: [AnalyticsEvent, AnalyticsProps | undefined][] = [];
const PENDING_MAX = 20;

/** What may travel: strings, numbers and booleans only, and strings kept short. */
function clean(props: AnalyticsProps | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props ?? {})) {
    if (typeof v === 'string') out[k] = v.slice(0, 60);
    else if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

function send(to: AnalyticsSink, event: AnalyticsEvent, props?: AnalyticsProps): void {
  to.capture(event, { ...clean(props), app_version: __APP_VERSION__ });
}

/**
 * Loads PostHog (once) and starts it with the settings that keep it anonymous. A failure to load it
 * leaves analytics off: it is never a reason for the app to complain.
 */
async function load(
  env: AnalyticsEnv,
  loader: () => Promise<{
    default: AnalyticsSink & { init: (key: string, options: object) => unknown };
  }>,
): Promise<void> {
  try {
    const { default: posthog } = await loader();
    posthog.init(env.VITE_POSTHOG_KEY?.trim() ?? '', {
      api_host: env.VITE_POSTHOG_HOST?.trim() || DEFAULT_POSTHOG_HOST,
      // Nothing is kept on the device, nothing is collected by itself, and nobody is identified.
      persistence: 'memory',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      person_profiles: 'identified_only',
      ip: false,
      respect_dnt: true,
      advanced_disable_flags: true,
      loaded: (ph: AnalyticsSink) => {
        // The consent may have gone while the library was loading.
        if (!wanted) ph.opt_out_capturing();
      },
    });
    sink = posthog;
    if (wanted) {
      posthog.opt_in_capturing();
      for (const [event, props] of pending) send(posthog, event, props);
    }
    pending = [];
  } catch {
    sink = null;
    pending = [];
  }
}

/**
 * The player's consent, as it stands now: yes starts analytics (if this build has a key), no stops it
 * and forgets the visit's identifier. Safe to call as often as the consent is read.
 */
export function setAnalyticsConsent(
  granted: boolean,
  env: AnalyticsEnv = import.meta.env,
  loader: () => Promise<{
    default: AnalyticsSink & { init: (key: string, options: object) => unknown };
  }> = () => import('posthog-js'),
): void {
  wanted = granted && analyticsConfigured(env);
  if (!wanted) {
    pending = [];
    sink?.opt_out_capturing();
    sink?.reset();
    return;
  }
  if (sink) {
    sink.opt_in_capturing();
    return;
  }
  loading ??= load(env, loader).finally(() => {
    loading = null;
  });
}

/**
 * Sends an event. Without a consent (and a key) it is dropped, nothing is kept behind it; with one, while
 * the library is still being fetched, a few are kept for the moment it is there.
 */
export function track(event: AnalyticsEvent, props?: AnalyticsProps): void {
  if (!wanted) return;
  if (sink) send(sink, event, props);
  else if (pending.length < PENDING_MAX) pending.push([event, props]);
}

/** For the tests: forget everything. */
export function resetAnalyticsForTests(): void {
  sink = null;
  loading = null;
  wanted = false;
  pending = [];
}

/** For the tests: wait for the library to be loaded. */
export async function analyticsReadyForTests(): Promise<void> {
  await loading;
}
